import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const MOBILE_MENU_BODY_CLASS = 'dash-mobile-menu-open';

const MenuStateContext = createContext(null);

export const useMenuState = () => {
    const context = useContext(MenuStateContext);
    if (!context) {
        throw new Error('useMenuState must be used within a MenuStateProvider');
    }
    return context;
};

export const MenuStateProvider = ({ children }) => {
    const [isMenuCollapsed, setIsMenuCollapsed] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const location = useLocation();

    const toggleMenuCollapse = useCallback(() => {
        setIsMenuCollapsed(prev => !prev);
    }, []);

    const setMenuCollapsed = useCallback((collapsed) => {
        setIsMenuCollapsed(collapsed);
    }, []);

    const openMobileMenu = useCallback(() => {
        setIsMobileMenuOpen(true);
    }, []);

    const closeMobileMenu = useCallback(() => {
        setIsMobileMenuOpen(false);
    }, []);

    const toggleMobileMenu = useCallback(() => {
        setIsMobileMenuOpen(prev => !prev);
    }, []);

    useEffect(() => {
        closeMobileMenu();
    }, [location.pathname, closeMobileMenu]);

    useEffect(() => {
        if (isMobileMenuOpen) {
            document.body.classList.add(MOBILE_MENU_BODY_CLASS);
        } else {
            document.body.classList.remove(MOBILE_MENU_BODY_CLASS);
        }

        return () => {
            document.body.classList.remove(MOBILE_MENU_BODY_CLASS);
        };
    }, [isMobileMenuOpen]);

    const value = {
        isMenuCollapsed,
        toggleMenuCollapse,
        setMenuCollapsed,
        isMobileMenuOpen,
        openMobileMenu,
        closeMobileMenu,
        toggleMobileMenu,
    };

    return (
        <MenuStateContext.Provider value={value}>
            {children}
        </MenuStateContext.Provider>
    );
};
